import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Button, Space } from "antd";
import { UTCTimePicker } from "../../../../../../components/DateTimeInput";
import "../../../../../../components/common/EditableCellGrid/EditableCellGrid.css";
import { buildDateTimeWithTime } from "../../../lib/timeUtils";

dayjs.extend(utc);

const isStdColumn = (columnKey) => columnKey === "stdTime" || columnKey === "std";

const NEGATIVE_OFFSETS = [
    { label: "-5m", minutes: -5 },
    { label: "-15m", minutes: -15 },
    { label: "-1h", minutes: -60 },
    { label: "-5h", minutes: -300 },
];

const POSITIVE_OFFSETS = [
    { label: "+5m", minutes: 5 },
    { label: "+15m", minutes: 15 },
    { label: "+1h", minutes: 60 },
    { label: "+5h", minutes: 300 },
];

const TIME_EDITOR_POPUP_CLASS_NAME = "schedule-draft-rdg-time-editor-popup";
const ENTER_NAVIGATION = "next-row-same-column";
const TAB_NAVIGATION = "next-editable-cell-right";

function getNormalizedIso(value) {
    if (!value) {
        return null;
    }
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.toISOString() : null;
}

function isInsidePickerPopup(target) {
    return target instanceof Element && Boolean(target.closest(`.${TIME_EDITOR_POPUP_CLASS_NAME}`));
}

const FlightTimeCellEditor = ({ row, column, onRowChange, onClose, onCommitNavigation }) => {
    const editorRootRef = useRef(null);
    const pickerRef = useRef(null);
    const closeCommittedRef = useRef(false);
    const blurCommitTimerRef = useRef(null);
    const unmountCommitTimerRef = useRef(null);
    const rawFlight = row.rawFlight || {};
    const isStd = isStdColumn(column.key);
    const rawKey = isStd ? "std" : "sta";
    const initialTime = rawFlight[rawKey];
    const baseValue = initialTime || rawFlight.std;
    const [currentTime, setCurrentTime] = useState(initialTime);
    const latestIsoRef = useRef(initialTime || null);

    const focusEditorInput = useCallback(() => {
        window.setTimeout(() => {
            pickerRef.current?.focusAndSelect?.();
        }, 0);
    }, []);

    const commitPickerInputValue = useCallback(() => pickerRef.current?.commitInputValue?.() !== false, []);

    const clearUnmountCommitTimer = useCallback(() => {
        if (unmountCommitTimerRef.current !== null) {
            window.clearTimeout(unmountCommitTimerRef.current);
            unmountCommitTimerRef.current = null;
        }
    }, []);

    const commitValue = useCallback((isoValue, options = {}) => {
        if (!isoValue) {
            if (!initialTime) {
                return false;
            }
            options.beforeCommit?.();
            onRowChange({ ...row, [column.key]: "", rawFlight: { ...rawFlight, [rawKey]: null } }, true);
            return true;
        }

        const nextTime = dayjs.utc(isoValue);
        if (!nextTime.isValid()) {
            return false;
        }

        const normalizedIso = nextTime.toISOString();
        if (getNormalizedIso(initialTime) === normalizedIso) {
            return false;
        }

        const nextRawFlight = { ...rawFlight, [rawKey]: normalizedIso };
        options.beforeCommit?.();
        onRowChange(
            {
                ...row,
                [column.key]: nextTime.format("HH:mm"),
                rawFlight: nextRawFlight,
            },
            true,
        );
        return true;
    }, [column.key, initialTime, onRowChange, rawFlight, rawKey, row]);

    const commitAndClose = useCallback((isoValue = latestIsoRef.current, options = {}) => {
        if (closeCommittedRef.current) {
            return;
        }

        clearUnmountCommitTimer();
        closeCommittedRef.current = true;
        const navigationIntent = options.navigationIntent || null;
        commitValue(isoValue, {
            beforeCommit: () => {
                if (navigationIntent) {
                    onCommitNavigation?.({ row, column, direction: navigationIntent });
                }
            },
        });
        onClose(false, true);
    }, [clearUnmountCommitTimer, column, commitValue, onClose, onCommitNavigation, row]);

    const cancelAndClose = useCallback(() => {
        if (closeCommittedRef.current) {
            return;
        }

        clearUnmountCommitTimer();
        closeCommittedRef.current = true;
        onClose(false, false);
    }, [clearUnmountCommitTimer, onClose]);

    const handlePickerChange = (value) => {
        if (!value) {
            latestIsoRef.current = null;
            setCurrentTime(null);
            commitAndClose(null);
            return;
        }

        const nextIso = buildDateTimeWithTime(baseValue, value);
        latestIsoRef.current = nextIso;
        setCurrentTime(nextIso);
    };

    const handleOffset = (minutes) => {
        const nextValue = dayjs.utc(latestIsoRef.current || currentTime || baseValue).add(minutes, "minute");
        const nextIso = nextValue.toISOString();
        latestIsoRef.current = nextIso;
        setCurrentTime(nextIso);
    };

    const handleClose = () => {
        if (!commitPickerInputValue()) {
            focusEditorInput();
            return;
        }
        commitAndClose(latestIsoRef.current);
    };

    const scheduleBlurCommit = useCallback((event) => {
        const nextTarget = event?.relatedTarget;
        if ((nextTarget instanceof Node && editorRootRef.current?.contains(nextTarget)) || isInsidePickerPopup(nextTarget)) {
            return;
        }

        if (blurCommitTimerRef.current !== null) {
            window.clearTimeout(blurCommitTimerRef.current);
        }
        blurCommitTimerRef.current = window.setTimeout(() => {
            blurCommitTimerRef.current = null;
            const activeElement = document.activeElement;
            if ((activeElement instanceof Node && editorRootRef.current?.contains(activeElement)) || isInsidePickerPopup(activeElement)) {
                return;
            }
            if (!commitPickerInputValue()) {
                focusEditorInput();
                return;
            }
            commitAndClose(latestIsoRef.current);
        }, 0);
    }, [commitAndClose, commitPickerInputValue, focusEditorInput]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            cancelAndClose();
            return;
        }

        if (event.key !== "Enter" && event.key !== "Tab") {
            return;
        }
        if (event.key === "Tab" && event.shiftKey) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (!commitPickerInputValue()) {
            focusEditorInput();
            return;
        }
        commitAndClose(latestIsoRef.current, {
            navigationIntent: event.key === "Enter" ? ENTER_NAVIGATION : TAB_NAVIGATION,
        });
    }, [cancelAndClose, commitAndClose, commitPickerInputValue, focusEditorInput]);

    useEffect(() => {
        pickerRef.current?.focusAndSelect?.();
    }, []);

    useEffect(() => {
        const handleDocumentMouseDown = (event) => {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            if (editorRootRef.current?.contains(target) || isInsidePickerPopup(target)) {
                return;
            }
            if (!commitPickerInputValue()) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation?.();
                focusEditorInput();
                return;
            }
            commitAndClose(latestIsoRef.current);
        };

        document.addEventListener("mousedown", handleDocumentMouseDown, true);
        return () => {
            document.removeEventListener("mousedown", handleDocumentMouseDown, true);
        };
    }, [commitAndClose, commitPickerInputValue, focusEditorInput]);

    useEffect(() => {
        const handleDocumentKeyDown = (event) => {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            if (!editorRootRef.current?.contains(target) && !isInsidePickerPopup(target)) {
                return;
            }
            handleKeyDown(event);
        };

        document.addEventListener("keydown", handleDocumentKeyDown, true);
        return () => {
            document.removeEventListener("keydown", handleDocumentKeyDown, true);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        clearUnmountCommitTimer();

        return () => {
            clearUnmountCommitTimer();
            if (blurCommitTimerRef.current !== null) {
                window.clearTimeout(blurCommitTimerRef.current);
                blurCommitTimerRef.current = null;
            }
            if (!closeCommittedRef.current) {
                unmountCommitTimerRef.current = window.setTimeout(() => {
                    unmountCommitTimerRef.current = null;
                    if (closeCommittedRef.current) {
                        return;
                    }
                    commitPickerInputValue();
                    closeCommittedRef.current = true;
                    commitValue(latestIsoRef.current);
                }, 0);
            }
        };
    }, [clearUnmountCommitTimer, commitPickerInputValue, commitValue]);

    return (
        <div
            ref={editorRootRef}
            className="editable-cell-editor schedule-draft-rdg-time-cell-editor"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="schedule-draft-rdg-time-cell-editor-panel">
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <UTCTimePicker
                        ref={pickerRef}
                        value={currentTime || initialTime}
                        defaultDate={baseValue}
                        onChange={handlePickerChange}
                        onBlur={scheduleBlurCommit}
                        onKeyDown={handleKeyDown}
                        format="HH:mm"
                        showDate={true}
                        minuteStep={5}
                        style={{ height: 36, borderRadius: 0, width: "100%" }}
                        popupClassName={TIME_EDITOR_POPUP_CLASS_NAME}
                        getPopupContainer={() => document.body}
                        selectOnFocus
                        onOpenChange={(open) => {
                            if (!open) {
                                handleClose();
                            }
                        }}
                    />
                    <Space direction="vertical" size={2} style={{ width: "100%" }}>
                        <Space size={2} style={{ justifyContent: "center", width: "100%" }}>
                            {NEGATIVE_OFFSETS.map(({ label, minutes }) => (
                                <Button key={label} size="small" onClick={(event) => { event.stopPropagation(); handleOffset(minutes); }}>
                                    {label}
                                </Button>
                            ))}
                        </Space>
                        <Space size={2} style={{ justifyContent: "center", width: "100%" }}>
                            {POSITIVE_OFFSETS.map(({ label, minutes }) => (
                                <Button key={label} size="small" type="primary" onClick={(event) => { event.stopPropagation(); handleOffset(minutes); }}>
                                    {label}
                                </Button>
                            ))}
                        </Space>
                    </Space>
                </Space>
            </div>
        </div>
    );
};

export default FlightTimeCellEditor;
